/**
 * Thin wrapper over the Direct admin endpoints.
 *
 * Kept separate from the screens so the four pages share one view of the contract, and so a
 * backend path change is a one-file edit. Uses the panel's standard axios helpers, which already
 * carry the JWT and the /api base.
 */
import { get, postGeneric, putGeneric, patchGeneric, deleteGeneric } from '../../common/constants'
import { DIRECT_API } from './constants'

export const listPlans         = () => get(DIRECT_API.PLANS)
export const createPlan        = (body) => postGeneric(DIRECT_API.PLANS, body)
export const updatePlan        = (id, body) => putGeneric(DIRECT_API.PLAN(id), body)
export const repricePlan       = (id, body) => postGeneric(DIRECT_API.PLAN_REPRICE(id), body)
export const deletePlan        = (id) => deleteGeneric(DIRECT_API.PLAN(id))

export const listOffers        = () => get(DIRECT_API.OFFERS)
export const getOffer          = (id) => get(DIRECT_API.OFFER(id))
export const createOffer       = (body) => postGeneric(DIRECT_API.OFFERS, body)
/** Edit a DRAFT offer. The backend refuses once it is published. */
export const updateOffer       = (id, body) => putGeneric(DIRECT_API.OFFER(id), body)
export const publishOffer      = (id, body) => postGeneric(DIRECT_API.OFFER_PUBLISH(id), body)
export const withdrawOffer     = (id) => postGeneric(DIRECT_API.OFFER_WITHDRAW(id), {})

export const listSubscriptions = () => get(DIRECT_API.SUBSCRIPTIONS)
export const getSubscription   = (id) => get(DIRECT_API.SUBSCRIPTION(id))
export const getLedger         = (id, page = 0, size = 50) => get(DIRECT_API.SUB_LEDGER(id), { page, size })
export const getCharges        = (id) => get(DIRECT_API.SUB_CHARGES(id))
export const updateTerms       = (id, body) => patchGeneric(DIRECT_API.SUB_TERMS(id), body)
export const adjustLedger      = (id, body) => postGeneric(DIRECT_API.SUB_LEDGER(id), body)
export const activateForMerchant = (body) => postGeneric(DIRECT_API.SUB_ACTIVATE, body)
export const cancelSubscription = (id, reason) =>
  postGeneric(`${DIRECT_API.SUB_CANCEL(id)}?reason=${encodeURIComponent(reason)}`, {})
/** Renew now: charges the wallet or credit line, or records an offline payment when externalReference is set. */
export const renewSubscription = (id, body) => postGeneric(DIRECT_API.SUB_RENEW(id), body || {})

/** The merchant's plan email settings: reminder days (0 = off) and allowance alert levels. */
export const getNotificationSettings  = (merchantOid) => get(DIRECT_API.MERCHANT_NOTIFICATIONS(merchantOid))
export const saveNotificationSettings = (merchantOid, body) => putGeneric(DIRECT_API.MERCHANT_NOTIFICATIONS(merchantOid), body)

/** Run the daily plan job now (reminders, renewals, lapses). Safe to repeat on the same day. */
export const runDailyPlanJob = () => postGeneric(DIRECT_API.JOB_DAILY, {})

/** The default-account matrix: per courier + service type, the Prozo account a merchant inherits from. */
export const listDefaultAccounts    = () => get(DIRECT_API.DEFAULT_ACCOUNTS)
export const getDefaultAccountOptions = () => get(DIRECT_API.DEFAULT_ACCOUNT_OPTIONS)
/** Prozo accounts on this courier + service that can be the default. Empty when ProShip holds none. */
export const getDefaultAccountCandidates = (courierCode, serviceTypePrimary) =>
  get(DIRECT_API.DEFAULT_ACCOUNT_CANDIDATES, { courierCode, serviceTypePrimary })
export const createDefaultAccount   = (body) => postGeneric(DIRECT_API.DEFAULT_ACCOUNTS, body)
/** Default account and reason only — the courier + service pair is the row's key. */
export const updateDefaultAccount   = (id, body) => putGeneric(DIRECT_API.DEFAULT_ACCOUNT(id), body)
export const setDefaultAccountEnabled = (id, enabled) =>
  postGeneric(enabled ? DIRECT_API.DEFAULT_ACCOUNT_ENABLE(id) : DIRECT_API.DEFAULT_ACCOUNT_DISABLE(id), {})

/**
 * The backend returns merchant-readable sentences on 400 ("Insufficient wallet balance. This
 * costs ... short by ..."), wrapped by the global handler which prefixes "Input Data Error:".
 * Strip that prefix so the panel shows the sentence the service actually wrote.
 */
export const errText = (e) => {
  const raw = e?.response?.data?.message || e?.message || 'Something went wrong'
  return String(raw).replace(/^Input Data Error:\s*/i, '')
}
